import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { handleError } from 'reducers/ErrorReducer';
import { makeStyles } from '@material-ui/core/styles';
import Tooltip from 'components/Tooltip';
import InfoIcon from '@material-ui/icons/Info';
import { showReport } from 'reducers/DialogsReducer';
import QuickFilterSearch from '../components/QuickFilterSearch';
import { getTotalNumberOfItems, searchItems } from 'reducers/SearchReducer';
import Fab from '@material-ui/core/Fab';
import XGrid3 from 'components/XGrid3';
import LocalPrintshopRoundedIcon from '@material-ui/icons/LocalPrintshopRounded';
import RecordLimitAlert from 'Search/components/RecordLimitAlert';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import NoPermissionRedirect from 'components/NoPermissionRedirect';
import { checkReportPermission } from 'utils/functions';

const useStyles = makeStyles((theme) => ({
  filters: {
    display: 'flex',
    alignItems: 'baseline',
    marginBottom: theme.spacing(2),
    '& > div': {
      marginRight: theme.spacing(2),
    },
  },
  gridWrap: {
    width: '100%',
    boxSizing: 'border-box',
    height: 600,
  },
  filterWrap: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  filter: {
    width: '100%',
    marginBottom: '8px',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
  },
  panel: {
    '& .MuiDataGridPanelFooter-root': {
      display: 'none',
    },
  },
}));

const defaultColumns = [
  'ItemID',
  'Name',
  'Description',
  { field: 'Condition' },
  { field: 'Make' },
  { field: 'Model' },
  'ParentType',
  'SerialNumber',
  'ParentID',
  'Created',
  'CreatedBy',
  { field: 'Updated', format: 'date' },
  'UpdatedBy',
  'IsDeleted',
  'Deleted',
  'DeletedBy',
  'DeletedIP',
];

function Item(props) {
  const classes = useStyles();
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [selection, setSelection] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [itemColumns, setItemColumns] = useState(defaultColumns);
  const [printFlag, setPrintFlag] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const limit = 1000;
  const [page, setPage] = useState(0);
  const [showAlert, setShowAlert] = useState(false);
  const permissions = getFullPermissions('globals', 'Items', 'any');

  useEffect(() => {
    getItemData();
  }, []);

  useEffect(() => {
    if (filteredRows.length === rowCount) setShowAlert(false);
    else if (filteredRows.length >= limit) setShowAlert(true);
  }, [filteredRows, rowCount]);

  const getItemData = async (offset = 0, mergeRows = false) => {
    setLoaded(false);
    searchItems(offset, limit)
      .then((result) => {
        result.length ? setRowCount(result[0].Count) : setRowCount(0);
        //TRUE means, fetching next 1000 rows
        //so merging new rows with existing
        if (mergeRows) setRows((prevState) => [...prevState, ...processData(result)]);
        else {
          setRows(processData(result));
          setPage(0); //resetting page
        }
      })
      .catch(props.handleError)
      .finally(() => setLoaded(true));
  };

  const processData = (data) => {
    return data.map((row, idx) => {
      return {
        ...row,
        id: row.ptsItemID,
      };
    });
  };

  const viewDetails = () => {
    let item = filteredRows.find((row) => row.id === selection);
    if (!item || !checkReportPermission(34)) return;
    props.showReport({ id: 34, data: { ptsItemID: item.ptsItemID }, title: 'Item Details' });
  };

  const handlePrintClick = () => {
    setPrintFlag(true);
    setTimeout(() => setPrintFlag(false), 1000);
  };

  const onPageChange = () => {
    const offset = (page + 1) * limit;
    const mergingRows = true;
    getItemData(offset, mergingRows);
    setPage(page + 1);
  };
  if (!permissions.Read) return <NoPermissionRedirect />;
  return (
    <div>
      <h5>Item History</h5>
      <div className={classes.filterWrap}>
        <div className={classes.filterWrap}>
          <QuickFilterSearch rows={rows} columns={itemColumns} setFilteredRows={setFilteredRows} />
        </div>
        <div className={classes.actions}>
          <Tooltip title="Print list">
            <Fab size="small" color="secondary" onClick={handlePrintClick}>
              <LocalPrintshopRoundedIcon />
            </Fab>
          </Tooltip>
          {selection !== null && (
            <Tooltip title="View details">
              <Fab
                className="ml-2"
                size="small"
                color="secondary"
                disabled={!checkReportPermission(34)}
                onClick={viewDetails}>
                <InfoIcon />
              </Fab>
            </Tooltip>
          )}
        </div>
      </div>
      {showAlert && (
        <RecordLimitAlert
          handleNextPage={onPageChange}
          rowCount={rowCount}
          currentRows={filteredRows.length}
        />
      )}
      <XGrid3
        name="Item History"
        columns={itemColumns}
        setColumns={setItemColumns}
        rows={filteredRows}
        loading={!loaded}
        setSelection={setSelection}
        printFlag={printFlag}
        viewDetails={viewDetails}
        refresh={getItemData}
      />
    </div>
  );
}

export default connect(null, {
  handleError,
  showReport,
})(Item);
